import { useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { FaTimes, FaPlus, FaClock } from "react-icons/fa";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { slotRequest } from "../../../../../../models/request/counselling-request";
import { useAvailabilitySlotMutation } from "../../../../../../services/counselling-service";

const dayOptions = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];
const unitOptions = [
  "DAILY",
  "WEEKLY",
  "BI_WEEKLY",
  "MONTHLY",
  "QUARTERLY",
  "BI_ANNUALLY",
  "YEARLY",
];

export const AddAvailabilityContent = () => {
  const navigate = useNavigate();
  const [userId] = useState(sessionStorage.getItem("userId"));
  const id = Number(userId);

  const [availabilitySlot, setAvailabilitySlot] = useState<slotRequest>({
    mentorId: id,
    numberOfTimes: {
      value: 0,
      unit: "HOURLY",
    },
    times: [
      {
        daysDate: [],
        day: "MONDAY",
        time: [
          {
            startTime: "",
            endTime: "",
          },
        ],
      },
    ],
  });

  const [dateInput, setDateInput] = useState<string>("");

  // Use the mutation hook
  const [addSlot, { isLoading: submitting }] = useAvailabilitySlotMutation();

  const handleNumberOfTimesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAvailabilitySlot({
      ...availabilitySlot,
      numberOfTimes: {
        ...availabilitySlot.numberOfTimes,
        value: Number.parseInt(e.target.value) || 0,
      },
    });
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAvailabilitySlot({
      ...availabilitySlot,
      numberOfTimes: {
        ...availabilitySlot.numberOfTimes,
        unit: e.target.value,
      },
    });
  };

  const handleDayChange = (index: number, value: string) => {
    const updatedTimes = [...availabilitySlot.times];
    updatedTimes[index].day = value;
    setAvailabilitySlot({
      ...availabilitySlot,
      times: updatedTimes,
    });
  };

  const handleDateChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const date = e.target.value;
    setDateInput("");

    if (!date) return;

    const updatedTimes = [...availabilitySlot.times];

    // Check if date already exists
    if (!updatedTimes[index].daysDate.includes(date)) {
      updatedTimes[index].daysDate = [...updatedTimes[index].daysDate, date];
    }

    setAvailabilitySlot({
      ...availabilitySlot,
      times: updatedTimes,
    });
  };

  const removeDate = (timeIndex: number, dateIndex: number) => {
    const updatedTimes = [...availabilitySlot.times];
    updatedTimes[timeIndex].daysDate = updatedTimes[timeIndex].daysDate.filter(
      (_, idx) => idx !== dateIndex
    );
    setAvailabilitySlot({
      ...availabilitySlot,
      times: updatedTimes,
    });
  };

  const handleTimeChange = (
    timeIndex: number,
    slotIndex: number,
    field: "startTime" | "endTime",
    value: string
  ) => {
    const updatedTimes = [...availabilitySlot.times];
    updatedTimes[timeIndex].time[slotIndex][field] = value;
    setAvailabilitySlot({
      ...availabilitySlot,
      times: updatedTimes,
    });
  };

  const addTimeSlot = (timeIndex: number) => {
    const updatedTimes = [...availabilitySlot.times];
    updatedTimes[timeIndex].time.push({
      startTime: "",
      endTime: "",
    });
    setAvailabilitySlot({
      ...availabilitySlot,
      times: updatedTimes,
    });
  };

  const removeTimeSlot = (timeIndex: number, slotIndex: number) => {
    const updatedTimes = [...availabilitySlot.times];
    updatedTimes[timeIndex].time = updatedTimes[timeIndex].time.filter(
      (_, idx) => idx !== slotIndex
    );
    setAvailabilitySlot({
      ...availabilitySlot,
      times: updatedTimes,
    });
  };

  const addDay = () => {
    setAvailabilitySlot({
      ...availabilitySlot,
      times: [
        ...availabilitySlot.times,
        {
          daysDate: [],
          day: "MONDAY",
          time: [
            {
              startTime: "",
              endTime: "",
            },
          ],
        },
      ],
    });
  };

  const removeDay = (index: number) => {
    setAvailabilitySlot({
      ...availabilitySlot,
      times: availabilitySlot.times.filter((_, idx) => idx !== index),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Format time values to match the expected format
      const formattedPayload = {
        ...availabilitySlot,
        times: availabilitySlot.times.map((timeSlot) => ({
          ...timeSlot,
          time: timeSlot.time.map((t) => ({
            startTime: t.startTime,
            endTime: t.endTime,
          })),
        })),
      };

      // Use the mutation hook instead of fetch
      const response = await addSlot(formattedPayload).unwrap();
      const { code, message } = response!;
      if (code === 0) {
        toast.success(message);
        navigate(-1);
      } else {
        toast.error(message);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message);
    }
  };

  return (
    <Container className="py-4">
      <h6 className="mb-4">Availability Slot Scheduler</h6>

      <Form onSubmit={handleSubmit}>
        <Card className="mb-4 border-0">
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: "15px" }}>
                    Number of Times
                  </Form.Label>
                  <Form.Control
                    className="py-2"
                    style={{ fontSize: "14px" }}
                    type="number"
                    value={availabilitySlot.numberOfTimes.value}
                    onChange={handleNumberOfTimesChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: "15px" }}>Unit</Form.Label>
                  <Form.Select
                    className="py-2"
                    style={{ fontSize: "14px" }}
                    value={availabilitySlot.numberOfTimes.unit}
                    onChange={handleUnitChange}
                    required
                  >
                    {unitOptions.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <h6 className="mb-3">Availability Times</h6>

        {availabilitySlot.times.map((timeSlot, timeIndex) => (
          <Card key={timeIndex} className="mb-4 border-0">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>Day Schedule {timeIndex + 1}</div>
              {availabilitySlot.times.length > 1 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => removeDay(timeIndex)}
                >
                  <FaTimes />
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                {/* <Col md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "15px" }}>
                      Day of Week
                    </Form.Label>
                    <Form.Select
                      className="py-2"
                      style={{ fontSize: "14px" }}
                      value={timeSlot.day}
                      onChange={(e) =>
                        handleDayChange(timeIndex, e.target.value)
                      }
                      required
                    >
                      {dayOptions.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col> */}
                <Col md={12}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: "15px" }}>
                      Add Date
                    </Form.Label>
                    <Form.Control
                      className="py-2"
                      style={{ fontSize: "14px" }}
                      type="date"
                      value={dateInput}
                      onChange={(e) => setDateInput(e.target.value)}
                      onBlur={(e) =>
                        handleDateChange(
                          timeIndex,
                          e as React.ChangeEvent<HTMLInputElement>
                        )
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Display selected dates */}
              {timeSlot.daysDate.length > 0 && (
                <div className="mb-3">
                  <Form.Label>Selected Dates:</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {timeSlot.daysDate.map((date, dateIndex) => (
                      <div
                        key={dateIndex}
                        className="badge bg-primary d-flex align-items-center p-2"
                      >
                        {date}
                        <Button
                          variant="link"
                          className="p-0 ms-2 text-white"
                          onClick={() => removeDate(timeIndex, dateIndex)}
                        >
                          <FaTimes size={12} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <h6 className="mt-4 mb-3">Time Slots</h6>

              {timeSlot.time.map((slot, slotIndex) => (
                <Row key={slotIndex} className="mb-3 align-items-end">
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: "15px" }}>
                        Start Time
                      </Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaClock />
                        </span>
                        <Form.Control
                          style={{ fontSize: "14px" }}
                          className="py-2"
                          type="time"
                          value={slot.startTime}
                          onChange={(e) =>
                            handleTimeChange(
                              timeIndex,
                              slotIndex,
                              "startTime",
                              e.target.value
                            )
                          }
                          required
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: "15px" }}>
                        End Time
                      </Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaClock />
                        </span>
                        <Form.Control
                          style={{ fontSize: "14px" }}
                          className="py-2"
                          type="time"
                          value={slot.endTime}
                          onChange={(e) =>
                            handleTimeChange(
                              timeIndex,
                              slotIndex,
                              "endTime",
                              e.target.value
                            )
                          }
                          required
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    {timeSlot.time.length > 1 && (
                      <Button
                        variant="outline-danger"
                        className="w-100"
                        onClick={() => removeTimeSlot(timeIndex, slotIndex)}
                      >
                        <FaTimes />
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}

              <Button
                variant="outline-primary"
                className="mt-2 rounded-5"
                onClick={() => addTimeSlot(timeIndex)}
                style={{ fontSize: "12px" }}
              >
                <FaPlus className="me-1" /> Add Time Slot
              </Button>
            </Card.Body>
          </Card>
        ))}

        <div className="d-flex justify-content-between mb-4">
          <Button
            className="rounded-5 px-3"
            variant="outline-secondary"
            onClick={addDay}
            style={{ fontSize: "12px" }}
          >
            <FaPlus className="me-1" /> Add Another Day
          </Button>

          <Button
            className="rounded-5 px-5"
            type="submit"
            variant="primary"
            disabled={submitting}
            style={{ fontSize: "13px" }}
          >
            {submitting ? "Submitting..." : "Submit Availability Slot"}
          </Button>
        </div>
      </Form>
    </Container>
  );
};
